'use client';
import { gql } from '@apollo/client';
import { BrandReportGroupStatisticsBy, ReportSectionState } from '@mnd-frontend/graphql-schema';
import { GREY_900 } from '@mnd-frontend/ui/shared/tokens';
import { camelCase, groupBy } from 'lodash-es';
import { useMemo } from 'react';
import type { Report2VisibilityBenchmarkShareOfVoiceOverTimeFragment } from '../../../../../generated/types';
import { useReportDynamicData, useReportGraphHelpers, useReportStaticData } from '../../../hooks';
import { getGraphColors } from '../../../utils';
import { ShareOfVoiceOverTimeGraph } from '../../charts';
import { Insights, Section, SectionHeader } from '../../common';

export const REPORT2_VISIBILITY_BENCHMARK_SHARE_OF_VOICE_OVER_TIME_FRAGMENT = gql`
  fragment Report2VisibilityBenchmarkShareOfVoiceOverTime on ReportVisibilityBenchmarkShareOfVoiceOverTime {
    id
    title
    state
    groupBy
    metrics {
      startAt
      endAt
      month
      week
      mentions
      name
      searchProfileIds
    }
    insights2 {
      id
      title
      insight
      insightType
    }
  }
`;

const VisibilityBenchmarkShareOfVoiceOverTime = ({
  section,
}: {
  section: Report2VisibilityBenchmarkShareOfVoiceOverTimeFragment;
}) => {
  const { getPeriodLabel } = useReportGraphHelpers();
  const { brand } = useReportStaticData();
  const { brandColor } = useReportDynamicData();
  const metrics = section.metrics;
  const groupDataBy = section.groupBy;
  const groups = [{ id: brand }];

  const { data, mappings } = useMemo(() => {
    const groupedByName = Object.keys(
      groupBy(
        [...metrics].sort((a, b) => {
          if (a.name === brand) return -1;
          if (b.name === brand) return 1;

          return a.name?.localeCompare(b.name || '') || 0;
        }),
        'name',
      ),
    );
    const colors = [brandColor, ...getGraphColors(brandColor, groupedByName.length - 1)];

    const mappings = groupedByName.map((key, i) => {
      return {
        name: i === 0 ? brand : key,
        dataKey: camelCase(key),
        groupId: i === 0 ? brand : undefined,
        color: colors[i],
        searchProfileIds: metrics.find(m => m.name === key)?.searchProfileIds || [],
      };
    });

    const subGroup = groupDataBy === BrandReportGroupStatisticsBy.Month ? 'week' : 'month';
    const groupedByPeriod = groupBy(metrics, subGroup);

    const data = Object.entries(groupedByPeriod).map(([period, values]) => {
      const year = values.length
        ? new Date(values[0].startAt).getFullYear()
        : new Date().getFullYear();
      const name = getPeriodLabel(
        groupDataBy,
        year,
        Number.parseInt(period, 10),
        Number.parseInt(period, 10),
      );

      return values.reduce(
        (acc, value) =>
          value.name
            ? {
                ...acc,
                [camelCase(value.name)]: value.mentions,
                startAt: value.startAt,
                endAt: value.endAt,
              }
            : acc,
        { name },
      );
    });

    return { data, mappings } as {
      data: Record<string, string | number>[];
      mappings: { name: string; dataKey: string; color?: string; searchProfileIds: string[] }[];
    };
  }, [metrics, groupDataBy, brand, brandColor, getPeriodLabel]);

  return (
    <Section>
      <SectionHeader
        color={GREY_900}
        as="h3"
        variant="reportHeadlineLarge"
        section={section}
        isSubsection
      />
      <ShareOfVoiceOverTimeGraph
        loading={section.state !== ReportSectionState.Done}
        data={data}
        mappings={mappings}
        groups={groups}
      />
      <Insights insights={section.insights2} />
    </Section>
  );
};

export default VisibilityBenchmarkShareOfVoiceOverTime;
